import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef, EventEmitter,
  HostListener,
  Injector,
  Input,
  OnChanges, OnInit,
  Optional, Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { provideValueAccessor } from 's-ng-utils';
import { FormControl } from '@angular/forms';
import { Wrapper } from '../wrapper';
import { isNumeric, isObject } from 'rxjs/internal-compatibility';
import { isNullOrUndefined } from '@swimlane/ngx-datatable';
import { ClickEvent } from 'angular-star-rating';

@Component({
  selector: 'tag-input',
  templateUrl: './tag-input.component.html',
  styleUrls: ['./tag-input.component.scss'],
  providers: [provideValueAccessor(TagInputComponent)],
})
export class TagInputComponent<TModel> extends Wrapper implements OnChanges, OnInit {
  visible = true;
  selectable = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  autocompleteItems: Observable<TModel[]> | Observable<{ [key: string]: TModel[] }>;
  labelInputControl: FormControl = new FormControl();
  formControl = new FormControl();
  private filterChanged = new Subject<string>();

  selectedValues: any[] = [];   // ezek csak az értékek
  selectedItems: TModel[] = []; // ezek módosítás nélkül az eredeti objectumok

  @ViewChild('labelInput') labelInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild('autocompleteTrigger') autocompleteTrigger: MatAutocompleteTrigger;

  @Input() name: string;
  @Input() filterItems: (query: string) => TModel[];
  @Input() placeholder: string;
  @Input() items: TModel[] = [];
  @Input() removable = true;
  @Input() addOnBlur = true;
  @Input() createItemSupport = false;
  @Input() createItem: (text: string) => TModel;
  // ha be van kapcsolva akkor a megadott key alapján csoportosítja az autocomplete lemeket
  @Input() autocompleteGroupKey?: string = null;
  @Input() autocompleteGroupToString?: (T) => string;
  // Csak hogy működjön-e a kifele search query átadás
  @Input() searchOutput: boolean = false;
  @Output() searchTextChange: EventEmitter<string> = new EventEmitter();

  // a TModel objectumon belül milyen kulcs legyen használva a text érték meghatározására
  @Input() textPropertyName?: string = null;

  // a TModel objectumon belül milyen kulcs legyen használva a value érték meghatározására
  @Input() valuePropertyName?: string = null;

  // ez a függvény határozza meg, hogy a kapott elem milyen formában fog megjelenni a
  // kiválaszott elemek között és az autocomplete elemek között
  @Input() chipItemToString: (T) => string = (item) => {
    return this.textPropertyName ? item[this.textPropertyName] : item.toString();
  }

  @Input() autocompleteItemToString: (T) => string = (item) => {
    return this.textPropertyName ? item[this.textPropertyName] : item.toString();
  }

  @Input() canSelectItem: (T) => boolean = () => true;

  // ez a függvény határozza meg, hogy a megadott item milyen formában kerül a form értékre,
  // nem feltétlen objectum value-t akarunk ezért beállítható, hogy csak az id-t kezelje
  @Input() convertItemValue: (T) => string = (item) => {
    return this.valuePropertyName ? item[this.valuePropertyName] : item;
  }

  @HostListener('click', ['$event'])
  public onClick(e: ClickEvent) {
    // disabled inputnál nem lehet kinyitni, mert nem szabad, hogy módosítson rajta
    if (!this.formControl.disabled) {
      this.autocompleteTrigger.openPanel();
    }
  }

  constructor(@Optional() injector: Injector) {
    super(injector);

    this.autocompleteItems = this.labelInputControl.valueChanges.pipe(
      startWith(<string>null),
      map((query) => this._getAutocompleteItems(query)));
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.searchOutput) this.filterChanged.pipe(debounceTime(600)).subscribe(text => this.searchTextChange.emit(text));
  }

  getFormValue() {
    const value = this.selectedItems.map(this.convertItemValue);
    return value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this._reloadSelectedItems();
      this._refreshAutocompleteItems();
    }
  }

  onAddChip(event: MatChipInputEvent): void {
    // Ha nincs bekapcsolva a hozzáadás, akkor ilyenkor töröljük a beírt szöveget, mert nem fog bekerülni a listába
    if (!this.createItemSupport) {
      if (!this.matAutocomplete.isOpen) {
        this.labelInputControl.setValue(null);
        if (event.input) event.input.value = '';
      }

      // csak akkor tudunk új elemet létrehozni ha be van állítva a függvény
    } else if (!this.matAutocomplete.isOpen && this.createItem) {
      const input = event.input;
      const value = event.value;

      if ((value || '').trim()) {
        const item = this.createItem(value);
        if (item) {
          this.add(item);
        }
      }

      // Reset the input value
      if (input) {
        input.value = '';
      }

      this.labelInputControl.setValue(null);
    }
  }

  remove(item: TModel): void {
    const index = this.selectedItems.indexOf(item);
    if (index !== -1) {
      this.selectedItems.splice(index, 1);
      this.formControl.patchValue(this.getFormValue());
      this.formControl.markAsDirty()
      this._refreshAutocompleteItems();
    }
  }

  add(item: TModel) {
    // a megjelenítés miatt mindig tároljuk az egész objectumot
    this.selectedItems.push(item);

    // a form értéke nem feltétlen objectum, hanem lehet pl csak id
    this.formControl.patchValue(this.getFormValue());
    this.formControl.markAsDirty()
    this._refreshAutocompleteItems();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const newItem = event.option.value;
    const isSelected = this.selectedItems.indexOf(newItem) !== -1;
    // ha már ki van választva akkor nem lehet mégegyszer
    if (!isSelected && this.canSelectItem(newItem)) {
      this.add(event.option.value);
    }
    this.labelInput.nativeElement.value = '';
    this.labelInputControl.setValue(null);
  }

  private _reloadSelectedItems() {
    if (this.items && this.selectedValues) {
      if (this.valuePropertyName) {
        this.selectedItems = this.selectedValues
          .map(
            (newValue) => {
              return this.items.find(item => this.convertItemValue(item) === newValue);
            }
          ).filter(
            // kiszűrjük a nullos objectumokat mert ezekhez nem volt elem és nincs mit megjeleníteni
            (item) => !isNullOrUndefined(item)
          );
      } else {
        this.selectedItems = this.selectedValues;
      }
    }
  }

  private _refreshAutocompleteItems() {
    this.labelInputControl.setValue('');
  }

  private _getAutocompleteItems(query: string | null) {
    // leszűrjük a listát
    let filteredItems: TModel[];
    if (query) {
      filteredItems = this._filter(query);
      if (this.searchOutput) this.filterChanged.next(query);
    } else {

      // ha nincs megadva lista akkor addig nem dolgozzuk fel
      if (!this.items) {
        return [];
      }

      filteredItems = this.items.slice();
    }
    // kiszedjük a kiválaszott elemket
    const remainList = this._removeSelectedItems(filteredItems).slice();
    let result = {};
    if (!this.autocompleteGroupKey) {
      result = remainList;
    } else {
      remainList.forEach(item => {
        const groupValue = item[this.autocompleteGroupKey];
        if (!result[groupValue]) {
          result[groupValue] = [];
        }

        result[groupValue].push(item);
      });
    }

    return result;
  }


  private _isEqual(value, searchValue): boolean {
    // ha van id mindkét elemben akkor az alapján nézzük
    if ((isObject(value) && isNumeric(value.id)) &&
      (isObject(value) && isNumeric(searchValue.id))) {
      return value.id === searchValue.id;
    }

    // teljes egyezés, pl string esetén
    return value === searchValue;
  }

  private _isSelected(search: TModel): boolean {
    return this.selectedItems.find(selectedItem => this._isEqual(selectedItem, search)) != null;
  }

  private _removeSelectedItems(items: TModel[]): TModel[] {
    // csak azokat adja vissza amik nincsenek kiválasztva (nincsenek a selectedItems tömbben)
    return items.filter(item => !this._isSelected(item));
  }

  /**
   * Visszaadja azokat az elemeket az items tömbből amik tartalmazzák a query paramétert.
   * @param query Keresési szöveg
   * @private
   */
  private _filter(query: string): TModel[] {
    // ha nincs keresés akkor az eredeti listát adjuk vissza
    if (!query || query.length === 0) {
      return this.items;
    }

    // ha van custom filter függvény akkor azt használjuk
    if (this.filterItems) {
      return this.filterItems(query);
    }

    // beépített kereső, csak string alapon megy
    const filterValue = query.toString().toLowerCase();
    return this.items.filter(item => this.chipItemToString(item).toLowerCase().indexOf(filterValue) !== -1);
  }

  writeValue(values: any) {
    // az értékek beállíthatóak átalakítás nélkül, mivel a ténylegesen kiválasztott objectumokat
    // ezek alapján keressük ki
    this.selectedValues = values || [];
    this._reloadSelectedItems();
    this._refreshAutocompleteItems();
  }
}
